// ConceptFrameGrid — Phase 4: the arc's CONCEPT, shared by the account workspace (inline per arc) and the arc
// detail page. The concept is the arc's FIRST ITEM'S REAL RUN, held at review — a video at the storyboard gate
// (beat 0's captioned card, raw frame on toggle), a carousel/still at the spread gate (candidates). Approving
// advances that run; no throwaway image. "Make concept" is explicit because it spends.
import { useState, useEffect, useCallback, useRef } from "react";
import { growthConceptApi, type ConceptState } from "../../api/growth.ts";
import { ProgressModal } from "@/components/ui/ProgressModal.tsx";
import { startProgress, atStep, finish, fail, type Progress } from "@/components/ui/progress.ts";
import { conceptProgress } from "./concept-progress.ts";
import { useProgress } from "@/components/ui/ProgressContext.tsx";


const card: React.CSSProperties = { border: "1px solid #e5e7eb", borderRadius: 10, padding: 14, background: "#fff", marginBottom: 10 };
const btn: React.CSSProperties = { border: "1px solid #d1d5db", borderRadius: 8, padding: "6px 12px", background: "#fff", cursor: "pointer", fontSize: 13 };
const btnPrimary: React.CSSProperties = { ...btn, background: "#2f855a", color: "#fff", border: 0 };
const muted: React.CSSProperties = { fontSize: 12, color: "#6b7280" };

const POLL_MS = 5000;

export function ConceptFrames({ arcId }: { arcId: string }) {
  const [concept, setConcept] = useState<ConceptState | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [raw, setRaw] = useState(false);
  const [editing, setEditing] = useState(false);
  const { track } = useProgress();
  // the progress window for "Make concept" (instead of a greyed-out button) — driven by the concept's own state
  const [prog, setProg] = useState<Progress | null>(null);
  const progRef = useRef<Progress | null>(null);
  const show = (p: Progress | null) => { progRef.current = p; setProg(p); };

  const load = useCallback(async (signal?: AbortSignal) => {
    try { setConcept((await growthConceptApi.read(arcId, signal)).concept); setErr(null); }
    catch (e) { if (!signal?.aborted) setErr(e instanceof Error ? e.message : String(e)); }
  }, [arcId]);

  useEffect(() => { const c = new AbortController(); void load(c.signal); return () => c.abort(); }, [load]);
  useEffect(() => {
    const p = progRef.current;
    if (!p || p.state !== "running" || !concept) return;
    const d = conceptProgress(concept);
    if (d.settled === "ready") show(finish(p, `${d.detail} — it's on the card below`));
    else if (d.settled === "failed") show(fail(p, d.detail));
    else show(atStep(p, d.step, d.detail, d.pct));
  }, [concept]);
  // Poll ONLY while the run is working toward its review gate (there is no push channel for this); stops on
  // ready / approved / failed / cancelled / none.
  useEffect(() => {
    if (concept?.state !== "pending") return;
    const t = setInterval(() => { void load(); }, POLL_MS);
    return () => clearInterval(t);
  }, [concept?.state, load]);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true); setErr(null);
    try { await fn(); await load(); } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };
  const modal = <ProgressModal p={prog} onClose={() => show(null)} />;
  if (!concept) return <div style={card}><div style={muted}>{err ?? "Loading concept…"}</div></div>;

  const header = <div style={{ fontWeight: 700, marginBottom: 6 }}>{modal}Concept</div>;
  const reject = <button style={btn} disabled={busy} onClick={() => act(() => track("Rejecting the concept", growthConceptApi.reject(arcId), { detail: "clears it so you can make a new one" }))}>Reject</button>;
  const error = err && <div style={{ color: "#b91c1c", fontSize: 12, marginTop: 6 }}>{err}</div>;

  if (concept.state === "none") return (
    <div style={card}>{header}
      <div style={{ ...muted, marginBottom: 8 }}>
        Makes the arc's first item for real, up to its review step — a video's storyboard frames, or a
        carousel/still's candidates. Spends image generation now; clips only after you approve.
      </div>
      <button style={btnPrimary} disabled={busy} onClick={() => {
        show(atStep(startProgress("Making the concept", ["Start the concept run", "The engine makes the first item (script → frames), usually 1–4 min", "Ready for your review"]), 0, "starting…"));
        void (async () => {   // (not act(): its error lands in state after this closure — the window must see it now)
          setBusy(true); setErr(null);
          try { await growthConceptApi.make(arcId); if (progRef.current) show(atStep(progRef.current, 1, "the run has started…")); await load(); }
          catch (e) { const m = e instanceof Error ? e.message : String(e); setErr(m); if (progRef.current) show(fail(progRef.current, m)); }
          finally { setBusy(false); }
        })();
      }}>{busy ? "Starting…" : "Make concept"}</button>
      {error}
    </div>
  );
  if (concept.state === "pending") return <div style={card}>{header}<div style={muted}>Making the concept… (checks every {POLL_MS / 1000}s)</div>{error}</div>;
  if (concept.state === "approved") return <div style={card}>{header}<div style={muted}>✓ Concept approved — the first item is in production. Produce the arc for the rest.</div></div>;
  if (concept.state === "failed" || concept.state === "cancelled") return (
    <div style={card}>{header}
      <div style={{ ...muted, marginBottom: 8 }}>The concept run {concept.state}{concept.reason ? `: ${concept.reason}` : ""}.</div>
      {reject}<span style={{ ...muted, marginLeft: 8 }}>clears it, so you can make a new one</span>{error}
    </div>
  );

  if (concept.state !== "ready") return null;   // every other state returned above — this narrows the union
  if (concept.kind === "video") {
    const b = concept.beat;
    const src = raw ? b.imageUrl : (b.cardUrl ?? b.imageUrl);
    return (
      <div style={card}>{header}
        {src ? <img src={src} alt="concept frame" style={{ width: "100%", maxWidth: 280, borderRadius: 8, display: "block" }} /> : <div style={muted}>(no frame)</div>}
        {b.cardUrl && b.imageUrl && <button style={{ ...btn, marginTop: 6 }} onClick={() => setRaw(!raw)}>{raw ? "Show captioned card" : "Show raw frame"}</button>}
        <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
          <button style={btnPrimary} disabled={busy} onClick={() => act(() => track("Approving the concept", growthConceptApi.approve(arcId), { detail: "the clips are made next", done: () => "approved — the first item is in production" }))}>Approve (clips next)</button>
          <button style={btn} disabled={busy || b.regenerationsUsed >= b.regenerationsMax} onClick={() => setEditing(true)}>
            Regenerate ({b.regenerationsMax - b.regenerationsUsed} left)</button>
          {reject}
        </div>
        {error}
        {editing && <PromptEditModal title="Regenerate the concept frame" initial={b.subjectText ?? ""}
          onCancel={() => setEditing(false)}
          onSubmit={(subjectText) => { setEditing(false); void act(() => track("Regenerating the concept frame", growthConceptApi.regenerate(arcId, { beatId: b.beatId, subjectText }), { detail: subjectText ? `with: ${subjectText}` : "a new take" })); }} />}
      </div>
    );
  }
  return (
    <div style={card}>{header}
      <div style={{ ...muted, marginBottom: 8 }}>Pick the candidate to make — the pick is final for this run.</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 10 }}>
        {concept.candidates.map((c) => {
          const src = c.previewUrl ?? c.previewUrls?.[0] ?? null;
          return (
            <div key={c.candidateId} style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
              {src ? <img src={src} alt="candidate" style={{ width: "100%", display: "block" }} /> : <div style={{ ...muted, padding: 8 }}>(no preview)</div>}
              <button style={{ ...btnPrimary, width: "100%", borderRadius: 0 }} disabled={busy}
                onClick={() => act(() => track("Picking this candidate", growthConceptApi.approve(arcId, c.candidateId), { detail: "it goes on to the final version" }))}>Pick this</button>
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <button style={btn} disabled={busy} onClick={() => act(() => growthConceptApi.regenerate(arcId))}>New candidates</button>
        {reject}
      </div>
      {error}
    </div>
  );
}

// Reusable prompt/subject edit modal — used by the concept-frame regenerate (full prompt) and can be
// reused by the storyboard beat regenerate (subject edit).
export function PromptEditModal({ title, initial, onCancel, onSubmit }: {
  title: string; initial: string; onCancel: () => void; onSubmit: (value: string) => void;
}) {
  const [value, setValue] = useState(initial);
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 }} onClick={onCancel}>
      <div style={{ background: "#fff", borderRadius: 10, padding: 18, width: "min(560px, 92vw)" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>{title}</div>
        <div style={{ ...muted, marginBottom: 8 }}>Edit the prompt, then regenerate this one image.</div>
        <textarea value={value} onChange={(e) => setValue(e.target.value)} rows={6}
          style={{ width: "100%", boxSizing: "border-box", padding: 10, border: "1px solid #d1d5db", borderRadius: 8, fontFamily: "inherit", fontSize: 13 }} />
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 10 }}>
          <button style={btn} onClick={onCancel}>Cancel</button>
          <button style={btnPrimary} disabled={value.trim().length < 3} onClick={() => onSubmit(value.trim())}>Regenerate</button>
        </div>
      </div>
    </div>
  );
}
